/* eslint-disable react/prop-types */
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { round } from 'lodash';
import LandcoverBarChart from './BarChartComponent';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
  DatasetComponent
]);

const getOption = (data, unit) => {
  const categories = data.slice(1).map(row => row[0]); // Extract categories (e.g., Cotton, Wheat)
  const seriesNames = data[0].slice(1); // Extract series names (e.g., Rabi, Kharif)

  const colors = ['#5470C6', '#ffd000', '#ff4f4f', '#91cc75', '#c966ed'];
  const series = seriesNames.map((seriesName, index) => ({
    name: seriesName,
    type: 'bar',
    data: data.slice(1).map(row => row[index + 1]), // Extract data for each series
    itemStyle: {
      borderRadius: [3, 3, 0, 0]
    },
    barWidth: '25px'
  }));

  return {
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      transitionDuration: 0,
      valueFormatter: function (value) {
        return value + ` ${unit}`;
      }
    },
    legend: {
      data: seriesNames,
      left: 'left',
      itemWidth: 20,
      itemHeight: 15,
      borderRadius: 0,
      icon: 'circle',
      inactiveColor: getColor('gray-400'),
      textStyle: { color: getColor('gray-700') }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: value => value,
        color: getColor('gray-500')
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: getColor('gray-200')
        }
      }
    },
    xAxis: {
      type: 'category',
      axisLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisLabel: {
        color: getColor('gray-500')
      },
      axisTick: { show: true },
      splitLine: { show: false },
      data: categories
    },
    series: series,
    grid: { right: 15, left: '12%', bottom: '10%', top: '10%' }
  };
};

const SeasonalBarBody = ({ landCoverChartData, onLegendSelect }) => {
  const { data, series, unit } = landCoverChartData;

  // Transform the data to the format expected by the chart
  const chartData = [
    ['', ...Object.keys(data)], // The first row is the series names (e.g., Cotton, Wheat)
    ...series.map((seriesName, index) => [
      seriesName,
      ...Object.values(data).map(item => item[index] || 0) // Map each series value with default 0 if not found
    ])
  ];

  const seriesNames = chartData[0].slice(1); // Extract the names of the series

  const chartRef = useRef(null);

  // Event handler for legend selection change
  const onChartReady = chart => {
    chart.on('legendselectchanged', params => {
      const selectedLegend = Object.keys(params.selected).filter(
        key => params.selected[key]
      );
      if (selectedLegend.length > 0) {
        onLegendSelect(selectedLegend[selectedLegend.length - 1]);
      }
    });
  };

  useEffect(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      onChartReady(chartInstance);

      // Initially select the last legend (if any)
      if (seriesNames.length > 0) {
        onLegendSelect(seriesNames[seriesNames.length - 1]);
      }
    }
  }, [seriesNames]);

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getOption(chartData, unit)} // Pass the unit to the chart for tooltip display
      onChartReady={onChartReady}
      style={{ minHeight: '25.75rem' }}
    />
  );
};

SeasonalBarBody.propTypes = {
  landCoverChartData: PropTypes.object.isRequired,
  onLegendSelect: PropTypes.func.isRequired
};

export default SeasonalBarBody;
